import React, { Component } from "react";
import PropTypes from "prop-types";

import { colors } from "../../../../theme";
import Particle from "./CovidHerdImmunityParticle";
import {
  typeEnum,
  FPS,
  UPDATE_INTERVAL,
  DURATION,
  QUARANTINE_WIDTH,
  AVERAGE_VEL_SETTING,
  RADIUS,
  getWallCoords,
} from "./CovidHerdImmunityConstants";

class CovidHerdImmunitySimulation extends Component {
  constructor(props) {
    super(props);
    this.canvas = React.createRef();
  }

  componentDidMount() {
    this.particleCanvas = this.canvas.current;

    const resizeCanvas = () => {
      if (window.innerWidth > 1599) {
        this.particleCanvas.width = 778;
      } else if (window.innerWidth > 1023) {
        this.particleCanvas.width = 640;
      } else if (window.innerWidth > 767) {
        this.particleCanvas.width = 595;
      } else {
        this.particleCanvas.width = window.innerWidth - 52;
      }
    };

    window.addEventListener("resize", resizeCanvas, false);
    resizeCanvas();
    this.init(this.props.start);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.start !== this.props.start && this.props.start) {
      this.start();
    }
    if (this.props.start && prevProps.restart !== this.props.restart) {
      clearInterval(this.simInterval);
      clearTimeout(this.simTimeout);
      this.init(true);
    }
  }

  componentWillUnmount() {
    clearInterval(this.simInterval);
    clearTimeout(this.simTimeout);
  }

  init(startAfterInit) {
    const { quarantine, immunePopulation, confinedPopulation } = this.props;

    const PARTICLES_NBR = 300;

    this.particles = [];

    // Init RECOVERED this.particles
    const immuneTarget = Math.round(immunePopulation * PARTICLES_NBR);
    for (let i = 0; i < immuneTarget; i++) {
      this.particles.push(
        new Particle(
          this.particleCanvas,
          typeEnum.RECOVERED,
          quarantine,
          i < confinedPopulation * immuneTarget
        )
      );
    }

    // Init SICK particle
    this.sickInitParticle = new Particle(
      this.particleCanvas,
      typeEnum.SICK,
      quarantine
    );
    this.particles.push(this.sickInitParticle);

    // Init HEALTHY this.particles
    for (let i = 0; i < PARTICLES_NBR - immuneTarget - 1; i++) {
      this.particles.push(
        new Particle(
          this.particleCanvas,
          typeEnum.HEALTHY,
          quarantine,
          i < confinedPopulation * (PARTICLES_NBR - immuneTarget)
        )
      );
    }

    if (startAfterInit) {
      this.start();
    }
  }

  start() {
    const { setLastSeries, prob, quarantine } = this.props;

    // const CFR = 0.02;
    const CFR = 0;
    const ctx1 = this.particleCanvas.getContext("2d");

    const timeseries = [];
    let frames = 0;

    this.sickInitParticle.makeSick();

    this.simInterval = setInterval(() => {
      frames++;

      //clear canvas
      ctx1.fillStyle = "#f5fbff";
      ctx1.fillRect(
        0,
        0,
        this.particleCanvas.width,
        this.particleCanvas.height
      );

      ctx1.fillStyle = colors.textColor;
      const QUARANTINE_HOLE_LENGTH =
        this.particleCanvas.height * (1 - quarantine);
      const WALL_COORDS = getWallCoords(this.particleCanvas);
      ctx1.fillRect(
        WALL_COORDS[0],
        0,
        QUARANTINE_WIDTH,
        this.particleCanvas.height / 2.0 - QUARANTINE_HOLE_LENGTH / 2.0
      );
      ctx1.fillRect(
        WALL_COORDS[0],
        this.particleCanvas.height / 2.0 + QUARANTINE_HOLE_LENGTH / 2.0,
        QUARANTINE_WIDTH,
        this.particleCanvas.height / 2.0 - QUARANTINE_HOLE_LENGTH / 2.0
      );

      let runningParticles = 0;
      for (const particle of this.particles) {
        runningParticles += particle.confined ? 0 : 1;
      }

      const averageSpeed =
        this.particles.reduce(
          (acc, cur) => acc + Math.sqrt(cur.vx ** 2 + cur.vy ** 2),
          0
        ) / runningParticles;
      const correctionFactor = AVERAGE_VEL_SETTING / averageSpeed;

      for (const particle of this.particles) {
        particle.draw(correctionFactor);
      }

      // check for recovery
      for (let i = 0; i < this.particles.length; i++) {
        if (this.particles[i].type === typeEnum.SICK) {
          const delta = new Date().getTime() - this.particles[i].sick_time;
          if (delta > 0) {
            this.particles[i].type =
              Math.random() < CFR ? typeEnum.DEAD : typeEnum.RECOVERED;
          }
        }
      }

      // check for collision betweet this.particles
      for (let i = 0; i < this.particles.length; i++) {
        for (let j = i + 1; j < this.particles.length; j++) {
          if (this.particles[i].confined && this.particles[j].confined) {
            continue;
          }

          //collision between this.particles
          if (
            Math.abs(this.particles[i].x - this.particles[j].x) < 2 * RADIUS &&
            Math.abs(this.particles[i].y - this.particles[j].y) < 2 * RADIUS
          ) {
            const squared =
              (this.particles[i].x - this.particles[j].x) ** 2 +
              (this.particles[i].y - this.particles[j].y) ** 2;

            if (squared < 4 * RADIUS ** 2) {
              const dvx = this.particles[i].vx - this.particles[j].vx;
              const dvy = this.particles[i].vy - this.particles[j].vy;

              const dx = this.particles[j].x - this.particles[i].x;
              const dy = this.particles[j].y - this.particles[i].y;

              //squared = dx**2 + dy**2; //variablenname already taken!
              const scalar = dvx * dx + dvy * dy;

              const dvx2 = (dx * scalar) / squared;
              const dvy2 = (dy * scalar) / squared;

              if (!this.particles[i].confined) {
                this.particles[i].vx -= dvx2;
                this.particles[i].vy -= dvy2;
              }

              if (!this.particles[j].confined) {
                this.particles[j].vx += dvx2;
                this.particles[j].vy += dvy2;
              }

              //avoid this.particles 'sticking to each other'
              const versatz = RADIUS / Math.sqrt(squared) - 1 / 2;

              this.particles[i].x -= dx * versatz;
              this.particles[i].y -= dy * versatz;

              this.particles[j].x += dx * versatz;
              this.particles[j].y += dy * versatz;

              // contaminate ?

              if (Math.random() < prob) {
                this.particles[i].collisions++;
                this.particles[j].collisions++;

                if (
                  this.particles[i].type === typeEnum.SICK &&
                  this.particles[j].type === typeEnum.HEALTHY
                ) {
                  this.particles[j].makeSick();
                } else if (
                  this.particles[j].type === typeEnum.SICK &&
                  this.particles[i].type === typeEnum.HEALTHY
                ) {
                  this.particles[i].makeSick();
                }
              }
            }
          }
        }
      }

      const counts = { 0: 0, 1: 0, 2: 0, 3: 0 };
      for (let i = 0; i < this.particles.length; i++) {
        counts[this.particles[i].type]++;
      }

      if (frames % UPDATE_INTERVAL === 0) {
        timeseries.push(counts);
        setLastSeries && setLastSeries(counts);
      }
    }, 1000 / FPS);

    this.simTimeout = setTimeout(
      () => clearInterval(this.simInterval),
      DURATION * 1000
    );
  }

  render() {
    return (
      <canvas
        ref={this.canvas}
        width={this.width}
        height="480"
        aria-label="Simulation de propagation du COVID-19"
      />
    );
  }
}

CovidHerdImmunitySimulation.propTypes = {
  start: PropTypes.bool.isRequired,
  restart: PropTypes.number.isRequired,
  prob: PropTypes.number.isRequired,
  quarantine: PropTypes.number.isRequired,
  immunePopulation: PropTypes.number.isRequired,
  confinedPopulation: PropTypes.number.isRequired,
  setLastSeries: PropTypes.func,
};

CovidHerdImmunitySimulation.defaultProps = {
  start: false,
  restart: 0,
  prob: 1,
  quarantine: 0,
  immunePopulation: 0,
  confinedPopulation: 0,
};

export default CovidHerdImmunitySimulation;
